import { cn } from '@invent/wl-ui-kit-next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/** One choice: the value the caller keeps and the words a reader picks it by. */
export interface IChoice {
  value: string;
  label: string;
}

interface IChoiceSelectProps {
  value: string;
  onChange: (value: string) => void;
  choices: readonly IChoice[];
  disabled?: boolean;
  /** The id of the visible words naming the box — a `span`, never a wrapping `<label>`: the
   *  kit's select keeps a hidden native one inside a form, and a label around both names two. */
  labelledBy?: string;
  'aria-label'?: string;
  className?: string;
}

/** The prefix every value carries inside the kit's select. */
const PREFIX = 'v:';

/** The list opened under its box: as wide as the box, no taller than `18rem` or the room left. */
const LIST_CLASS = [
  'max-h-[min(18rem,var(--radix-select-content-available-height))]',
  'w-[var(--radix-select-trigger-width)]'
].join(' ');

/**
 * A short list chosen from inside a dialog (SR-455): the kit's select, opened under its box and
 * exactly as wide, its long names wrapped inside that width and the list scrolling past `18rem`.
 * The browser's own `<select>` drew its list at the width of the longest option, over the dialog
 * and past its frame, with a long cycle code it could not wrap; the client asked for the dialogs to
 * keep inside their borders.
 *
 * **Every value goes to the kit under a prefix**, `filter-bar.tsx`' rule: Radix keeps `''` for
 * "nothing chosen" and refuses an item of that value, and `''` is a real choice here ("No cycle",
 * no layout). The caller sees its own values only. **A value without the prefix is dropped**:
 * inside a form Radix keeps a hidden native select, and when the value arrives before the options
 * it registers (a list read after the dialog opened) that select answers `change` with `''`, which
 * would have wiped the choice just made.
 */
export function ChoiceSelect({
  value,
  onChange,
  choices,
  disabled,
  labelledBy,
  'aria-label': ariaLabel,
  className
}: IChoiceSelectProps) {
  return (
    <Select
      value={PREFIX + value}
      disabled={disabled}
      onValueChange={(next) => {
        if (next.startsWith(PREFIX)) onChange(next.slice(PREFIX.length));
      }}
    >
      <SelectTrigger
        aria-labelledby={labelledBy}
        aria-label={ariaLabel}
        className={cn('h-9 min-w-0 bg-white text-left [&>span]:truncate', className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={LIST_CLASS}>
        {choices.map((choice) => (
          <SelectItem
            key={choice.value}
            value={PREFIX + choice.value}
            className="break-words whitespace-normal"
          >
            {choice.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
